import React, { useContext } from "react";
import { getDurationDisplay, getTZAbbreviation, requireBookingDataFields } from "../../../../utils";
import { gap, marginLeft, marginRight, timeFormats, typography } from "../../../styles";
import { faSync } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { BookingData } from "@src/BookingTools/App/AppDataProvider/types";
import { PartRequired } from "@src/BookingTools/App/utils/types";
import { addMinutes } from "date-fns";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AppContext } from "../../../../AppDataProvider/AppDataProvider";
import { applyOpacity } from "../../../styles/utils";
type BookingInfoBookingData = PartRequired<BookingData, "organisation">;
const SummaryItem = styled.div`
  display: flex;
  align-items: center;

  ${typography.small}

  >*:not(:last-child) {
    ${marginRight.s}
  }
`;
const DurationItem = styled.div`
  display: flex;
  align-items: center;

  ${gap.s}
  ${typography.small}
`;
const SlotDateTime = styled.div`
  display: flex;
  flex-direction: column;
`;
const SlotTime = styled.div`
  display: flex;
`;
const TimezoneText = styled.div`
  ${marginLeft.s}
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
`;
const DurationLabel = styled.label`
  text-transform: capitalize;
  color: ${({
  theme
}) => applyOpacity(theme.colours.dark, 0.6)};
`;
const Occurrences = styled.div`
  ${typography.smallBold}
`;
function BookingInfo() {
  const {
    i18n,
    t
  } = useTranslation();
  const {
    appData
  } = useContext(AppContext);
  const {
    bookingData
  } = appData;
  requireBookingDataFields<BookingInfoBookingData>(bookingData, ["organisation"]);
  const {
    duration,
    organisation,
    recurringBooking,
    resource,
    selectedBookings,
    timezone,
    startDate
  } = bookingData;

  // If booking onto an existing booking where the booking time has been changed and its duration no longer matches duration on service, then use start and end time of booking to calculate duration
  let calculatedDuration = duration;
  if (selectedBookings && selectedBookings.length === 1) {
    const [selectedBooking] = selectedBookings;
    const {
      start,
      end
    } = selectedBooking;
    const startTime = new Date(start);
    const endTime = new Date(end);
    const durationInMinutes = (endTime.getTime() - startTime.getTime()) / (1000 * 60);
    calculatedDuration = {
      minutes: durationInMinutes
    };
  }
  function getTimeSlotDisplay() {
    if (!startDate) return null;
    const timezoneAbbreviation = getTZAbbreviation({
      date: startDate,
      locale: i18n.language,
      timezone
    });
    return <SummaryItem data-sentry-element="SummaryItem" data-sentry-component="getTimeSlotDisplay" data-sentry-source-file="BookingInfo.tsx">
        <SlotDateTime data-sentry-element="SlotDateTime" data-sentry-source-file="BookingInfo.tsx">
          <div>
            {startDate.toLocaleDateString(i18n.language, /* eslint-disable sort-keys */{
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric",
            timeZone: timezone
          } /* eslint-enable sort-keys */)}
          </div>
          {calculatedDuration ? <SlotTime>
              {startDate.toLocaleString(i18n.language, {
            ...timeFormats.default,
            timeZone: timezone
          })}
              {" – "}
              {addMinutes(startDate, calculatedDuration.minutes).toLocaleTimeString(i18n.language, {
            ...timeFormats.default,
            timeZone: timezone
          })}
              <TimezoneText>{timezoneAbbreviation}</TimezoneText>
            </SlotTime> : null}
        </SlotDateTime>
      </SummaryItem>;
  }
  function getRecurrenceDisplay() {
    if (!recurringBooking || !selectedBookings || !startDate) return null;
    const selectedOccurrences = selectedBookings.length;
    return <SummaryItem data-sentry-element="SummaryItem" data-sentry-component="getRecurrenceDisplay" data-sentry-source-file="BookingInfo.tsx">
        <Occurrences data-sentry-element="Occurrences" data-sentry-source-file="BookingInfo.tsx">{selectedOccurrences}</Occurrences>
        <FontAwesomeIcon icon={faSync} data-sentry-element="FontAwesomeIcon" data-sentry-source-file="BookingInfo.tsx" />
        <div>
          {t("fromValue", {
          value: startDate.toLocaleDateString(i18n.language, /* eslint-disable sort-keys */{
            weekday: "short",
            day: "numeric",
            month: "short",
            year: "numeric"
          } /* eslint-enable sort-keys */)
        })}
        </div>
      </SummaryItem>;
  }
  return <div data-sentry-component="BookingInfo" data-sentry-source-file="BookingInfo.tsx">
      <SummaryItem data-sentry-element="SummaryItem" data-sentry-source-file="BookingInfo.tsx">{organisation.name}</SummaryItem>
      {resource ? <SummaryItem>{resource.name}</SummaryItem> : null}
      {getTimeSlotDisplay()}
      {calculatedDuration ? <DurationItem>
          <DurationLabel>{t("duration", {
          count: 1
        })}</DurationLabel>
          <span>{getDurationDisplay(calculatedDuration, t)}</span>
        </DurationItem> : null}
      {getRecurrenceDisplay()}
    </div>;
}
export default BookingInfo;